import { useState, type FormEvent } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useParams } from "react-router-dom"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { apiRequest, type ApiError } from "@/lib/api"
import type { Project, Segment, SegmentRules } from "@/types"
import {
  Plus,
  Filter,
  Loader2,
  Pencil,
  Trash2,
  Clock,
  AlertCircle,
  Users,
} from "lucide-react"

type SegmentsResponse = {
  segments: Segment[]
}

type SegmentPayload = {
  segment: Segment
}

type SegmentsPageProps = {
  currentProject: Project | null
}

type SegmentFormState = {
  name: string
  user_agent_categories: string[]
  countries: string
  url_contains: string
}

const USER_AGENT_OPTIONS = [
  { value: "android", label: "Android" },
  { value: "iphone", label: "iPhone" },
  { value: "ipad", label: "iPad" },
  { value: "ios", label: "iOS" },
  { value: "chrome", label: "Chrome" },
  { value: "safari", label: "Safari" },
  { value: "firefox", label: "Firefox" },
  { value: "edge", label: "Edge" },
  { value: "windows", label: "Windows" },
  { value: "macos", label: "macOS" },
  { value: "linux", label: "Linux" },
]

const emptyForm = (): SegmentFormState => ({
  name: "",
  user_agent_categories: [],
  countries: "",
  url_contains: "",
})

const parseCsv = (value: string) =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)

const formToRules = (form: SegmentFormState): SegmentRules => {
  const countries = parseCsv(form.countries).map((country) => country.toUpperCase())
  const urlContains = parseCsv(form.url_contains)

  const rules: SegmentRules = {}
  if (form.user_agent_categories.length > 0) rules.user_agent_categories = form.user_agent_categories
  if (countries.length > 0) rules.countries = countries
  if (urlContains.length > 0) rules.url_contains = urlContains

  return rules
}

const rulesToForm = (segment: Segment): SegmentFormState => ({
  name: segment.name,
  user_agent_categories: segment.rules.user_agent_categories || [],
  countries: (segment.rules.countries || []).join(", "),
  url_contains: (segment.rules.url_contains || []).join(", "),
})

const formatDate = (dateString: string | null) => {
  if (!dateString) return "Nunca"
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return "Nunca"
  return new Intl.DateTimeFormat("pt-BR", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date)
}

const SegmentRulesBadges = ({ segment }: { segment: Segment }) => (
  <div className="flex max-w-[min(100%,28rem)] flex-wrap gap-1">
    {(segment.rules.user_agent_categories || []).map((value) => (
      <Badge key={`ua-${segment.id}-${value}`} variant="outline" className="text-xs font-normal">
        UA: {value}
      </Badge>
    ))}
    {(segment.rules.countries || []).map((value) => (
      <Badge key={`country-${segment.id}-${value}`} variant="secondary" className="text-xs font-normal">
        {value}
      </Badge>
    ))}
    {(segment.rules.url_contains || []).map((value) => (
      <Badge key={`url-${segment.id}-${value}`} variant="outline" className="text-xs font-normal">
        URL: {value}
      </Badge>
    ))}
    {!segment.rules.user_agent_categories?.length &&
    !segment.rules.countries?.length &&
    !segment.rules.url_contains?.length ? (
      <span className="text-muted-foreground">—</span>
    ) : null}
  </div>
)

type SegmentFormContentProps = {
  form: SegmentFormState
  setForm: React.Dispatch<React.SetStateAction<SegmentFormState>>
  onSubmit: (e: FormEvent) => void
  isSaving: boolean
  error: ApiError | null
  onCancel: () => void
  title: string
  description: string
  submitLabel: string
  submitIcon: React.ReactNode
}

const SegmentFormContent = ({
  form,
  setForm,
  onSubmit,
  isSaving,
  error,
  onCancel,
  title,
  description,
  submitLabel,
  submitIcon,
}: SegmentFormContentProps) => {
  const toggleUserAgent = (value: string, checked: boolean) => {
    setForm((prev) => {
      if (checked) {
        return {
          ...prev,
          user_agent_categories: Array.from(new Set([...prev.user_agent_categories, value])),
        }
      }
      return {
        ...prev,
        user_agent_categories: prev.user_agent_categories.filter((item) => item !== value),
      }
    })
  }

  return (
    <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          <div className="py-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="segment-name">Nome</Label>
              <Input
                id="segment-name"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="ex.: Usuários Android nos EUA"
                className="text-base"
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="segment-countries">Países (separados por vírgula)</Label>
              <Input
                id="segment-countries"
                value={form.countries}
                onChange={(e) => setForm((prev) => ({ ...prev, countries: e.target.value }))}
                placeholder="US, BR, CA"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="segment-url">URL de inscrição contém (separados por vírgula)</Label>
              <Input
                id="segment-url"
                value={form.url_contains}
                onChange={(e) => setForm((prev) => ({ ...prev, url_contains: e.target.value }))}
                placeholder="pricing, /promo, blog"
              />
            </div>

            <div className="space-y-2">
              <Label>Categorias de User Agent</Label>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {USER_AGENT_OPTIONS.map((option) => (
                  <label key={option.value} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form.user_agent_categories.includes(option.value)}
                      onChange={(e) => toggleUserAgent(option.value, e.target.checked)}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>

            {error ? (
              <p className="text-sm text-destructive">{error.message}</p>
            ) : null}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSaving}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSaving || !form.name.trim()}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  {submitIcon}
                  {submitLabel}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
    </DialogContent>
  )
}

const SegmentsPage = ({ currentProject }: SegmentsPageProps) => {
  const { projectSlug } = useParams<{ projectSlug: string }>()
  const queryClient = useQueryClient()

  const [form, setForm] = useState<SegmentFormState>(emptyForm)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [editingSegment, setEditingSegment] = useState<Segment | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const basePath = `/api/projects/${encodeURIComponent(projectSlug || "")}/segments`

  const { data, isLoading, isError, error } = useQuery<SegmentsResponse, ApiError>({
    queryKey: ["segments", projectSlug],
    queryFn: () => apiRequest<SegmentsResponse>(basePath),
    enabled: Boolean(projectSlug),
  })

  const createMutation = useMutation<SegmentPayload, ApiError, { name: string; rules: SegmentRules }>({
    mutationFn: (payload) =>
      apiRequest<SegmentPayload>(basePath, {
        method: "POST",
        body: JSON.stringify({ segment: payload }),
      }),
    onSuccess: () => {
      setForm(emptyForm())
      setIsCreateOpen(false)
      queryClient.invalidateQueries({ queryKey: ["segments", projectSlug] })
    },
  })

  const updateMutation = useMutation<
    SegmentPayload,
    ApiError,
    { id: number; name: string; rules: SegmentRules }
  >({
    mutationFn: ({ id, ...payload }) =>
      apiRequest<SegmentPayload>(`${basePath}/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ segment: payload }),
      }),
    onSuccess: () => {
      setEditingSegment(null)
      setForm(emptyForm())
      queryClient.invalidateQueries({ queryKey: ["segments", projectSlug] })
    },
  })

  const deleteMutation = useMutation<void, ApiError, { id: number }>({
    mutationFn: ({ id }) =>
      apiRequest<void>(`${basePath}/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      setDeletingId(null)
      queryClient.invalidateQueries({ queryKey: ["segments", projectSlug] })
    },
    onError: () => {
      setDeletingId(null)
    },
  })

  const isSaving = createMutation.isPending || updateMutation.isPending
  const segments = data?.segments || []

  const handleCreateSubmit = (e: FormEvent) => {
    e.preventDefault()
    const name = form.name.trim()
    if (!name) return
    createMutation.mutate({ name, rules: formToRules(form) })
  }

  const handleUpdateSubmit = (e: FormEvent) => {
    e.preventDefault()
    const name = form.name.trim()
    if (!name || !editingSegment) return
    updateMutation.mutate({ id: editingSegment.id, name, rules: formToRules(form) })
  }

  const startEdit = (segment: Segment) => {
    setEditingSegment(segment)
    setForm(rulesToForm(segment))
  }

  const handleDelete = (segment: Segment) => {
    const confirmed = window.confirm(
      `Excluir segmento "${segment.name}"?\n\nEsta ação não pode ser desfeita.`
    )
    if (!confirmed) return
    setDeletingId(segment.id)
    deleteMutation.mutate({ id: segment.id })
  }

  return (
    <div className="flex flex-col gap-6 pb-8">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Segmentos</h1>
          <p className="mt-1 text-base text-muted-foreground">
            Salve filtros de audiência reutilizáveis por user agent, país e URL de inscrição.
          </p>
        </div>

        {currentProject ? (
          <Dialog
            open={isCreateOpen}
            onOpenChange={(open) => {
              if (!open) setForm(emptyForm())
              setIsCreateOpen(open)
            }}
          >
            <DialogTrigger asChild>
              <Button size="lg" className="gap-2">
                <Plus className="h-5 w-5" />
                Novo Segmento
              </Button>
            </DialogTrigger>
            <SegmentFormContent
              form={form}
              setForm={setForm}
              onSubmit={handleCreateSubmit}
              isSaving={createMutation.isPending}
              error={createMutation.error ?? null}
              onCancel={() => {
                setForm(emptyForm())
                setIsCreateOpen(false)
              }}
              title="Criar Novo Segmento"
              description="Defina filtros de audiência por user agent, país e URL de inscrição."
              submitLabel="Criar Segmento"
              submitIcon={<Plus className="mr-2 h-4 w-4" />}
            />
          </Dialog>
        ) : null}
      </div>

      {/* Error States */}
      {!currentProject ? (
        <div className="rounded-lg border-2 border-dashed bg-muted/20 p-8 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground/50" />
          <p className="mt-3 text-sm font-medium text-muted-foreground">
            Selecione um projeto para gerenciar segmentos
          </p>
        </div>
      ) : null}

      {isError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-800">{error?.message ?? "Falha ao carregar segmentos."}</p>
        </div>
      ) : null}

      {/* Loading State */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : null}

      {/* Empty State */}
      {!isLoading && !isError && segments.length === 0 && currentProject ? (
        <div className="rounded-lg border-2 border-dashed bg-muted/20 p-12 text-center">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
            <Filter className="h-10 w-10 text-primary" />
          </div>
          <h3 className="mt-4 text-lg font-semibold">Nenhum segmento ainda</h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            Crie filtros de audiência reutilizáveis por user agent, país e URL de inscrição. Use-os em
            fluxos de trabalho e transmissões.
          </p>
          <Button size="lg" className="mt-6 gap-2" onClick={() => setIsCreateOpen(true)}>
            <Plus className="h-5 w-5" />
            Criar Seu Primeiro Segmento
          </Button>
        </div>
      ) : null}

      {/* Segments list (table) */}
      {!isLoading && !isError && segments.length > 0 && currentProject ? (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {segments.length} {segments.length === 1 ? "segmento" : "segmentos"}
            </p>
          </div>

          <div className="rounded-lg border bg-card">
            <Table className="text-sm">
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[180px] pl-4">Segmento</TableHead>
                  <TableHead className="hidden sm:table-cell">Criado em</TableHead>
                  <TableHead className="hidden md:table-cell text-right">Inscritos</TableHead>
                  <TableHead className="min-w-[200px]">Filtros</TableHead>
                  <TableHead className="w-[1%] pr-4 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {segments.map((segment) => (
                  <TableRow key={segment.id}>
                    <TableCell className="pl-4 align-top">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                          <Filter className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold leading-tight">{segment.name}</p>
                          <p className="text-xs text-muted-foreground">ID: {segment.id}</p>
                          <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground sm:hidden">
                            <Clock className="h-3 w-3 shrink-0" />
                            <span>{formatDate(segment.created_at)}</span>
                          </div>
                          {typeof segment.subscriber_count === "number" ? (
                            <div className="mt-1 flex items-center gap-1 text-xs font-medium md:hidden">
                              <Users className="h-3 w-3 text-muted-foreground" />
                              <span>
                                {segment.subscriber_count.toLocaleString()}{" "}
                                {segment.subscriber_count === 1 ? "inscrito" : "inscritos"}
                              </span>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden align-top text-muted-foreground sm:table-cell">
                      <div className="flex items-center gap-2 text-xs">
                        <Clock className="h-3.5 w-3.5 shrink-0" />
                        <span>{formatDate(segment.created_at)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="hidden align-top text-right md:table-cell">
                      {typeof segment.subscriber_count === "number" ? (
                        <div className="inline-flex items-center justify-end gap-1.5 text-sm font-medium">
                          <Users className="h-3.5 w-3.5 text-muted-foreground" />
                          <span>{segment.subscriber_count.toLocaleString()}</span>
                          <span className="text-xs font-normal text-muted-foreground">
                            {segment.subscriber_count === 1 ? "inscrito" : "inscritos"}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="align-top whitespace-normal">
                      <SegmentRulesBadges segment={segment} />
                    </TableCell>
                    <TableCell className="pr-4 align-top text-right whitespace-nowrap">
                      <div className="flex flex-col items-end gap-1 sm:flex-row sm:justify-end sm:gap-2">
                        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => startEdit(segment)}>
                          <Pencil className="h-3.5 w-3.5" />
                          Editar
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                          disabled={deletingId === segment.id}
                          onClick={() => handleDelete(segment)}
                        >
                          {deletingId === segment.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      ) : null}

      {/* Edit Dialog */}
      <Dialog
        open={editingSegment !== null}
        onOpenChange={(open) => {
          if (!open) {
            setForm(emptyForm())
            setEditingSegment(null)
          }
        }}
      >
        <SegmentFormContent
          form={form}
          setForm={setForm}
          onSubmit={handleUpdateSubmit}
          isSaving={updateMutation.isPending}
          error={updateMutation.error ?? null}
          onCancel={() => {
            setForm(emptyForm())
            setEditingSegment(null)
          }}
          title="Editar Segmento"
          description="Atualize os filtros de audiência para este segmento."
          submitLabel="Atualizar Segmento"
          submitIcon={<Pencil className="mr-2 h-4 w-4" />}
        />
      </Dialog>
    </div>
  )
}

export default SegmentsPage
